import { ChangeDetectorRef, Component, HostListener, computed, effect, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { EngravingTypesService } from 'src/app/services/engraving-types/engraving-types.service';
import { EngravingType } from 'src/app/interfaces/engraving-type';
import { engravingSpecialChars } from 'src/app/utils/engraving.util';
import { IframeService } from 'src/app/services/iframe/iframe.service';
import { Navigation2Service } from '../../services/navigation2.service';

@Component({
  selector: 'app-engraving',
  templateUrl: './engraving.component.html',
  styleUrls: ['./engraving.component.scss']
})
export class EngravingComponent {
  private navigationService = inject(Navigation2Service);
  private iframeService = inject(IframeService);
  private engravingsService = inject(EngravingTypesService);
  private formBuilder = inject(FormBuilder);
  public selectedAttributeService = inject(SelectedAttributeService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  // engravingTypes = ENGRAVINGS;
  engravingTypes = computed(() => this.engravingsService.values());
  selectedEngravingType = computed(() => this.selectedAttributeService.getValue().engravingType);
  shouldAnimateEngravingTextIn = false;
  shouldAnimateEngravingTextOut = false;
  shouldAnimateSelection = false;
  shouldShowText = signal(!!this.selectedAttributeService.getValue().engravingType);
  engravingTextFieldValue = signal<string | null>(null);
  engravingDebounceTimer: ReturnType<typeof setTimeout> | undefined;

  formGroup = this.formBuilder.group({
    engravingText: [this.selectedAttributeService.getValue().engravingText, [Validators.maxLength(30)]]
  });

  constructor() {
    this.engravingTextFieldValue.set(this.selectedAttributeService.configuredItem().engravingText);
    effect(() => {
      const selectedType = this.selectedEngravingType();
      const text = this.selectedAttributeService.configuredItem().engravingText;
      if (selectedType && text) {
        this.navigationService.subtitle.set(`(${selectedType.displayName} Font) ${text}`);
      } else {
        this.navigationService.subtitle.set(null);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    if (this.formGroup.controls.engravingText.value) {
      this.formGroup.controls.engravingText.markAsTouched();
    } else {
      this.formGroup.controls.engravingText.valueChanges.subscribe((() => {
        this.formGroup.controls.engravingText.markAsTouched();
      }));
    }

    this.formGroup.controls.engravingText.valueChanges.subscribe(((value) => {
      const engravingSpecialCharsString = engravingSpecialChars.map(specialCharEntry => specialCharEntry.haloCharUnicode).join('');
      const sanitizerRegex = new RegExp(`[^${/\x20-\x21\x26-\x29\x2b-\x3f\x41-\x5d\x61-\x7a\x9e-\x9f/.toString()}${engravingSpecialCharsString}]`, 'g');
      const sanitizedValue = value?.replace(sanitizerRegex, '') ?? null;
      if (sanitizedValue !== value) {
        this.formGroup.controls.engravingText.setValue(sanitizedValue, { emitEvent: true });
      }
      this.engravingTextFieldValue.set(sanitizedValue ?? null);

      this.stopEngravingKeyDownPoll();
      this.startEngravingUpdates();
    }));
  }

  ngOnDestroy() {
    this.stopEngravingUpdates();
    this.stopEngravingKeyDownPoll();
    this.selectedAttributeService.mutateEngravingText(this.engravingTextFieldValue());
  }

  ngAfterViewInit() {
    this.shouldAnimateSelection = true;
    this.changeDetectorRef.detectChanges();
  }

  stopEngravingKeyDownPoll() {
    if (this.engravingDebounceTimer !== undefined) {
      clearTimeout(this.engravingDebounceTimer);
      this.engravingDebounceTimer = undefined;
    }
  }

  startEngravingUpdates() {
    this.engravingDebounceTimer = setTimeout(() => {
      this.engravingDebounceTimer = undefined;
      this.selectedAttributeService.mutateEngravingText(this.engravingTextFieldValue());
      this.stopEngravingKeyDownPoll();
      this.sendEngravingChangeMessage();
    }, 400);
  }

  stopEngravingUpdates() {
    this.stopEngravingKeyDownPoll();
    this.sendEngravingChangeMessage();
  }

  // ngOnDestroy() {
    // const type = this.selectedAttributeService.getAttribute('engravingType')
    // const text = this.selectedAttributeService.getAttribute('engravingText')

    // if (type !== text && (!type || !text)) {
    //   this.selectedAttributeService.mutateEngravingText(null);
    //   this.selectedAttributeService.mutateEngravingType(null);
    // }
  // }

  getError(formControl: AbstractControl) {
    if (formControl.hasError('required')) {
      return 'Please enter a value for this field'
    }

    if (formControl.hasError('maxlength')) {
      return 'It looks like the text you entered is too long. Please shorten your input to 30 characters.'
    }

    const otherErrors = Object.keys(formControl.errors ?? {})

    if (otherErrors.length > 0) {
      const error = formControl.errors?.[otherErrors[0]];
      return error;
    }

    return '';
  }

  selectEngravingType(engravingType: EngravingType | null) {
    if (engravingType !== this.selectedEngravingType()) {
      if (engravingType) {
        this.shouldShowText.set(true);
      }
      this.shouldAnimateEngravingTextIn = this.selectedEngravingType() === null;
      this.selectedAttributeService.mutateEngravingType(engravingType);
    }

    if (engravingType === null) {
      this.shouldAnimateEngravingTextOut = !this.shouldAnimateEngravingTextIn;
    }

    this.sendEngravingChangeMessage();
  }

  @HostListener('animationend', ['$event.target'])
  viewContainerAnimationEnd(target: EventTarget | null) {
    if (target) {
      const castTarget = target as HTMLElement;
      if (castTarget.classList.contains('EngravingTextCard')) {
        this.shouldAnimateEngravingTextIn = false;
        this.shouldAnimateEngravingTextOut = false;

        if (castTarget.classList.contains('AnimateOut')) {
          this.shouldShowText.set(false);
        }
      }
    }
  }

  getStyle(engravingOption: string) {
    return engravingOption === 'Block' ? {font: '1rem "engraving_block"'} : {font: '1.6rem "engraving_script"', 'line-height': '1.2rem'};
  }

  appendToEngravingText(text: string) {
    this.formGroup.controls.engravingText.setValue(`${this.formGroup.controls.engravingText.value ?? ''}${text}`);
  }

  sendEngravingChangeMessage() {
    this.iframeService.sendMessage(JSON.stringify({ type: 'Benchmark::RingLab::engravingchange', value: { engravingType: this.selectedAttributeService.configuredItem().engravingType?.shortName ?? null, engravingText: this.formGroup.controls.engravingText.value } }));
  }
}
